import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { BRAND, NAV_LINKS } from "@/data/site";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/LanguageContext";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { List, X } from "@phosphor-icons/react";

function BrandLogo({ scrolled }) {
  return (
    <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
      <div className="overflow-hidden rounded-md bg-[#EEF2FB] p-1">
        <img src={BRAND.logo} alt="Unicorn Valley Group" className="h-9 w-9 object-contain" />
      </div>
      <div className="leading-tight">
        <p className={`font-heading text-[15px] font-bold tracking-tight ${scrolled ? "text-ink" : "text-white"}`}>
          Unicorn Valley
        </p>
        <p className="text-[10px] uppercase tracking-[0.2em] text-teal font-semibold">Global Trade Group</p>
      </div>
    </Link>
  );
}

function DesktopNav({ scrolled, t }) {
  return (
    <nav className="hidden items-center gap-6 xl:flex">
      {NAV_LINKS.map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          data-testid={`nav-${l.to.replace("/", "")}`}
          className={({ isActive }) =>
            `text-sm font-medium transition-colors hover:text-teal ${
              isActive ? "text-teal" : scrolled ? "text-slate-700" : "text-white/90"
            }`
          }
        >
          {t(`nav.${l.key}`)}
        </NavLink>
      ))}
    </nav>
  );
}

function AuthLinks({ user, logout, scrolled, t }) {
  if (!user) {
    return (
      <Link to="/login" data-testid="nav-login" className={`text-sm font-medium hover:text-teal ${scrolled ? "text-slate-700" : "text-white/90"}`}>
        {t("nav.login")}
      </Link>
    );
  }
  return (
    <>
      <Link
        to={user.role === "admin" ? "/admin" : "/dashboard"}
        data-testid="nav-dashboard"
        className={`text-sm font-medium hover:text-teal ${scrolled ? "text-slate-700" : "text-white/90"}`}
      >
        {user.role === "admin" ? t("nav.admin") : t("nav.dashboard")}
      </Link>
      <button onClick={logout} data-testid="nav-logout" className={`text-sm font-medium hover:text-teal ${scrolled ? "text-slate-500" : "text-white/70"}`}>
        {t("nav.logout")}
      </button>
    </>
  );
}

function MobileMenu({ user, logout, t }) {
  return (
    <div className="border-t border-slate-200 bg-white xl:hidden" data-testid="navbar-mobile-menu">
      <div className="flex flex-col px-5 py-4">
        {NAV_LINKS.map((l) => (
          <Link key={l.to} to={l.to} className="border-b border-slate-100 py-3 text-sm font-medium text-slate-700">
            {t(`nav.${l.key}`)}
          </Link>
        ))}
        <Link to="/contact?type=quote" className="mt-4 rounded-md bg-ink px-5 py-3 text-center text-sm font-semibold text-white">
          {t("nav.requestQuote")}
        </Link>
        {user ? (
          <button onClick={logout} className="mt-2 py-2 text-sm text-slate-500">{t("nav.logout")}</button>
        ) : (
          <Link to="/login" className="mt-2 py-2 text-center text-sm text-slate-600">{t("nav.login")}</Link>
        )}
      </div>
    </div>
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-slate-200 bg-white/85 backdrop-blur-md" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 lg:px-8">
        <BrandLogo scrolled={scrolled} />
        <DesktopNav scrolled={scrolled} t={t} />

        <div className="hidden items-center gap-3 xl:flex">
          <LanguageSwitcher light={scrolled} />
          <AuthLinks user={user} logout={logout} scrolled={scrolled} t={t} />
          <Link
            to="/contact?type=quote"
            data-testid="nav-cta-quote"
            className="rounded-md bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-ink-800 hover:-translate-y-0.5"
          >
            {t("nav.requestQuote")}
          </Link>
        </div>

        <div className="flex items-center gap-2 xl:hidden">
          <LanguageSwitcher light={scrolled || open} />
          <button
            className={`${scrolled || open ? "text-ink" : "text-white"}`}
            onClick={() => setOpen((o) => !o)}
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={28} /> : <List size={28} />}
          </button>
        </div>
      </div>

      {open && <MobileMenu user={user} logout={logout} t={t} />}
    </header>
  );
}
